/**
 * @file        ReviewConsole.jsx
 * @module      modules/kyb/manager/console
 * @description The KYB review console (shared HTML design, "KYC review
 *              console"): its own top bar and left menu, outside the ASAP app
 *              layout. Mounted by App.js at /kyb-manager/*:
 *
 *                /kyb-manager              Review queue
 *                /kyb-manager/case/:id     Case
 *                /kyb-manager/team         Team and SLA
 *
 *              Access is decided by the backend (KYB_REVIEWER_ROLES); a user
 *              without it sees a short message instead of the console.
 *
 * @param {{ onSignOut: () => void, backTo?: string }} props
 *        backTo — link shown in the top bar's account menu for roles that
 *        also have the ASAP app (e.g. air_admin → /dashboard).
 */

import React, { useCallback, useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { kybManagerApi } from "../api/kybManagerApi";
import QueuePage from "./QueuePage";
import CasePage from "./CasePage";
import TeamPage from "./TeamPage";
import { useSelector } from "react-redux";
import { ToastProvider } from "./ui";
import HeaderUserName from "../../../../components/common/HeaderUserName";
import NotificationBell from "../../../../components/common/Notifications/NotificationBell";
import { authuser } from "../../../../store/Authentication/AuthSlice";
import { toTitleCase } from "../../../../utils/formatName";
import logo from "../../../../assets/images/asap_logo.png";
import "./console.css";

const ROLE_LABEL = { kyb_manager: "KYB manager", kyb_executive: "KYB reviewer", air_admin: "ASAP admin", ocean_admin: "ASAP admin" };

function ChevronIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export default function ReviewConsole({ onSignOut, backTo }) {
  const location = useLocation();
  const appUser = useSelector(authuser);
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);
  const [counts, setCounts] = useState(null);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    kybManagerApi
      .me()
      .then((m) => setMe(m))
      .catch((e) => e.status === 401 && setDenied(true));
  }, []);

  // Menu counts: refreshed on every page change (the queue also reports them).
  const refreshCounts = useCallback(() => {
    kybManagerApi
      .cases({ tab: "review", limit: 1 })
      .then((d) => setCounts(d.counts))
      .catch(() => {});
  }, []);
  useEffect(refreshCounts, [location.pathname, refreshCounts]);

  const onQueue = !location.pathname.includes("/team");
  // KYB executives (a manager's team members): no Team and SLA page.
  const isExecutive = me?.role === "kyb_executive";
  const mineActive = new URLSearchParams(location.search).get("tab") === "mine";

  return (
    <div className="kybc">
      <ToastProvider>
        {denied ? (
          <div className="app">
            <main>
              <div className="pagehead">
                <div>
                  <h1>No access</h1>
                  <p>Your account can't open the KYC review console. Ask an ASAP admin to add you as a KYB manager.</p>
                </div>
              </div>
              <button className="btn ghost" onClick={onSignOut}>
                Sign out
              </button>
            </main>
          </div>
        ) : (
          <div className="app">
            {/* Top navigation in the shape of the ASAP app header: brand, the
                sections inline, then who is signed in and the way out. */}
            <header className="navbar bg-white shadow-cardsection rounded-xl w-full p-5">
              <div className="nav-left">
                <img src={logo} alt="ASAP" className="h-8" />
                <nav className="navlinks" aria-label="Sections">
                  <NavLink to="/kyb-manager" end className={() => (onQueue && !mineActive ? "on" : "")} aria-current={onQueue && !mineActive ? "page" : undefined}>
                    <span>Review queue</span>
                    <span className="count">{counts?.review ?? 0}</span>
                  </NavLink>
                  {!isExecutive ? (
                    <NavLink to="/kyb-manager/team" className={({ isActive }) => (isActive ? "on" : "")}>
                      <span>Team and SLA</span>
                    </NavLink>
                  ) : null}
                  <NavLink to="/kyb-manager?tab=mine" className={() => (mineActive ? "on" : "")}>
                    <span>My open cases</span>
                    <span className="count">{counts?.mine ?? 0}</span>
                  </NavLink>
                </nav>
              </div>
              {/* Same pieces as the app header: initials avatar, notification
                  bell, then "Hello! <name>" with the account menu. */}
              <div className="nav-right">
                <HeaderUserName user_name={{ full_name: appUser?.full_name || me?.name }} />
                <div className="notificationBell">
                  <NotificationBell />
                </div>
                <button className="tb-user" aria-label="Account menu" aria-expanded={menu} onClick={() => setMenu((x) => !x)}>
                  <span className="tb-who">
                    <b>Hello! {toTitleCase(appUser?.full_name || me?.name || "You")}</b>
                    <small>{ROLE_LABEL[me?.role || appUser?.userroles] || "KYC team"}</small>
                  </span>
                  <ChevronIcon />
                </button>
                {menu ? (
                  <div className="menu" role="menu" onMouseLeave={() => setMenu(false)}>
                    {backTo ? (
                      <a role="menuitem" href={backTo}>
                        Back to ASAP
                      </a>
                    ) : null}
                    <button role="menuitem" onClick={onSignOut}>
                      Logout
                    </button>
                  </div>
                ) : null}
              </div>
            </header>
            <main>
              <Routes>
                <Route index element={<QueuePage me={me} onCounts={setCounts} />} />
                <Route path="case/:id" element={<CasePage me={me} onChanged={refreshCounts} />} />
                <Route path="team" element={isExecutive ? <Navigate to="/kyb-manager" replace /> : <TeamPage me={me} />} />
                <Route path="*" element={<QueuePage me={me} onCounts={setCounts} />} />
              </Routes>
            </main>
          </div>
        )}
      </ToastProvider>
    </div>
  );
}
